import React, { useEffect, useRef, useState } from 'react';
import "./styles.css";
import { SVGAsset } from 'res';
import UIEditTextInput from '../UIEditTextInput';
import LayoutContainer from 'compid/shared/containers/LayoutContainer';
import CustomSelect, { CustomSelectItem } from 'compid/custom-ui/CustomSelect';

/**
 * @function UIEditMailAddressInput 
 * @param {STRING} className - Additional CSS class that we can pass to the UIEditMailAddressInput component.
 * @param {STRING} value - Pass the default value to the UIEditMailAddressInput component.
 * @param {STRING} placeHolder - Pass the placeHolder value to the UIEditMailAddressInput component.
 * @param {React.Component} children - React components that's being wrapped by the UIEditMailAddressInput component.
 * @returns {React.Component}
 * @example
 * <UIEditMailAddressInput className="some-class" value="Some value" placeHolder="Some value">
 *  <UIButton className="primary-button">Button name</UIButton>
 * </UIEditMailAddressInput>
 */
export default function UIEditMailAddressInput(props) {
  const className = props.className ?? "";
  const dataPoint = props.dataPoint ? { "data-point" : props.dataPoint } : {};
  const isEditing = String(props.isEditing) === "true";
  const allowEditing = String(props.allowEditing) === "true";
  const [ state, setState ] = useState({ value: props.value ?? "", edit: isEditing });
  const label = props.label ?? "";
  const changeLabel = `Change ${ label.toLowerCase() }`;
  const biller = props.biller ?? {};
  const onChange = props.onChange ?? (() => {});
  const hasAddress = !((biller.address === "") && (biller.city === "") && (biller.state === "") && (biller.zip === ""));
  const removeFormRowBottomMargin = !hasAddress;
  const formRowBottomMarginRemovedClass = removeFormRowBottomMargin ? "form-row-removed-bottom-margin" : "";
  const onEditClick = () => setState({ ...state, edit: true });

  const editLink = (
    <a onClick={ onEditClick }>
        <span className="flex-container flex-horizontal align-center edit-link">
            <SVGAsset id="EditIcon" />
            <p>Edit</p>
        </span>
    </a>
  );

  useEffect(() => setState({ ...state, value: props.value ?? "" }), [ props.value ]);

  return(
    <div className={`form-row flex-height uitextinput edit-email-address ${className}${ formRowBottomMarginRemovedClass }`}>
      {/* Default state. */}
      { !state.edit &&
         <>
          { hasAddress && 
            <>
              <div className="text-input-label">{ label }</div>
              <div className="flex-container flex-horizontal">
                  { allowEditing && 
                  <div className="flex-width gray-text">
                    <div >{ biller.address }</div>
                    <div>{ `${ biller.city }, ${ biller.state }, ${ biller.zip }` }</div>
                  </div>
                  }
                  { !allowEditing && 
                  <div className="flex-width gray-text">
                    { `You can't view or change the payment address. 
                      ${ biller.name }${ biller.number} will contact us directly if the address changes.` }
                  </div>
                  }
                  
                  { allowEditing && editLink }
              </div>
            </>
          }
          { !hasAddress && 
            <div className="flex-container flex-horizontal">
                <div className="flex-width text-input-label">{ label }</div>
                { allowEditing && editLink }
            </div>
          }
        </>
      }
      {/* Editing state. */}
      { state.edit && 
      <>
        <div className="text-input-label">{ changeLabel }</div>

        <UIEditTextInput isEditing="true" allowEditing="true" 
        placeHolder="Biller address 1" value={ biller.address } onChange={ onChange("address") } />
        
        <UIEditTextInput isEditing="true" allowEditing="true" removeBottomMargin="false" placeHolder="Address 2 (optional)" />

        <StateCustomSelect biller={ biller } onChange={ onChange }/>
        
        <UIEditTextInput isEditing="true" allowEditing="true" 
        placeHolder="City" value={ biller.city } onChange={ onChange("city") } />

        <LayoutContainer className="zip-code">
          <UIEditTextInput isEditing="true" allowEditing="true" removeBottomMargin="true"
          placeHolder="ZIP code 5-digit" value={ biller.zip } onChange={ onChange("zip") } />

          <UIEditTextInput isEditing="true" allowEditing="true" removeBottomMargin="true"
          className="zip-four" placeHolder="+ZIP 4 (optional)" />
        </LayoutContainer>
      </>
      }
    </div>
  );
}

function StateCustomSelect(props) {
    const biller = props.biller ?? {};
    const onChange = props.onChange ?? (() => {});

    return (
        <div className="form-row">
            <CustomSelect label="State" helpText="" value={ biller.state } onChange={ onChange("state") }>
                <CustomSelectItem value="AL">Alabama (AL)</CustomSelectItem>
                <CustomSelectItem value="AK">Alaska (AK)</CustomSelectItem>
                <CustomSelectItem value="AS">American Samoa (AS)</CustomSelectItem>
                <CustomSelectItem value="AZ">Arizona (AZ)</CustomSelectItem>
                <CustomSelectItem value="AR">Arkansas (AR)</CustomSelectItem>
                <CustomSelectItem value="CA">California (CA)</CustomSelectItem>
                <CustomSelectItem value="CO">Colorado (CO)</CustomSelectItem>
                <CustomSelectItem value="CT">Connecticut (CT)</CustomSelectItem>
                <CustomSelectItem value="DE">Delaware (DE)</CustomSelectItem>
                <CustomSelectItem value="DC">District of Columbia (DC)</CustomSelectItem>
                <CustomSelectItem value="FM">Federated States Of Micronesia (FM)</CustomSelectItem>
                <CustomSelectItem value="FL">Florida (FL)</CustomSelectItem>
                <CustomSelectItem value="GA">Georgia (GA)</CustomSelectItem>
                <CustomSelectItem value="GU">Guam (GU)</CustomSelectItem>
                <CustomSelectItem value="HI">Hawaii (HI)</CustomSelectItem>
                <CustomSelectItem value="ID">Idaho (ID)</CustomSelectItem>
                <CustomSelectItem value="IL">Illinois (IL)</CustomSelectItem>
                <CustomSelectItem value="IN">Indiana (IN)</CustomSelectItem>
                <CustomSelectItem value="IA">Iowa (IA)</CustomSelectItem>
                <CustomSelectItem value="KS">Kansas (KS)</CustomSelectItem>
                <CustomSelectItem value="KY">Kentucky (KY)</CustomSelectItem>
                <CustomSelectItem value="LA">Louisiana (LA)</CustomSelectItem>
                <CustomSelectItem value="ME">Maine (ME)</CustomSelectItem>
                <CustomSelectItem value="MH">Marshall Islands (MH)</CustomSelectItem>
                <CustomSelectItem value="MD">Maryland (MD)</CustomSelectItem>
                <CustomSelectItem value="MA">Massachusetts (MA)</CustomSelectItem>
                <CustomSelectItem value="MI">Michigan (MI)</CustomSelectItem>
                <CustomSelectItem value="MN">Minnesota (MN)</CustomSelectItem>
                <CustomSelectItem value="MS">Mississippi (MS)</CustomSelectItem>
                <CustomSelectItem value="MO">Missouri (MO)</CustomSelectItem>
                <CustomSelectItem value="MY">Montana (MT)</CustomSelectItem>
                <CustomSelectItem value="NE">Nebraska (NE)</CustomSelectItem>
                <CustomSelectItem value="NV">Nevada (NV)</CustomSelectItem>
                <CustomSelectItem value="NH">New Hampshire (NH)</CustomSelectItem>
                <CustomSelectItem value="NJ">New Jersey (NJ)</CustomSelectItem>
                <CustomSelectItem value="NM">New Mexico (NM)</CustomSelectItem>
                <CustomSelectItem value="NY">New York (NY)</CustomSelectItem>
                <CustomSelectItem value="NC">North Carolina (NC)</CustomSelectItem>
                <CustomSelectItem value="ND">North Dakota (ND)</CustomSelectItem>
                <CustomSelectItem value="MP">Northern Mariana Islands (MP)</CustomSelectItem>
                <CustomSelectItem value="OH">Ohio (OH)</CustomSelectItem>
                <CustomSelectItem value="OK">Oklahoma (OK)</CustomSelectItem>
                <CustomSelectItem value="OR">Oregon (OR)</CustomSelectItem>
                <CustomSelectItem value="PW">Palau (PW)</CustomSelectItem>
                <CustomSelectItem value="PA">Pennsylvania (PA)</CustomSelectItem>
                <CustomSelectItem value="PR">Puerto Rico (PR)</CustomSelectItem>
                <CustomSelectItem value="RI">Rhode Island (RI)</CustomSelectItem>
                <CustomSelectItem value="SC">South Carolina (SC)</CustomSelectItem>
                <CustomSelectItem value="SD">South Dakota (SD)</CustomSelectItem>
                <CustomSelectItem value="TN">Tennessee (TN)</CustomSelectItem>
                <CustomSelectItem value="TX">Texas (TX)</CustomSelectItem>
                <CustomSelectItem value="UT">Utah (UT)</CustomSelectItem>
                <CustomSelectItem value="VT">Vermont (VT)</CustomSelectItem>
                <CustomSelectItem value="VI">Virgin Islands (VI)</CustomSelectItem>
                <CustomSelectItem value="VA">Virginia (VA)</CustomSelectItem>
                <CustomSelectItem value="WA">Washington (WA)</CustomSelectItem>
                <CustomSelectItem value="WV">West Virginia (WV)</CustomSelectItem>
                <CustomSelectItem value="WI">Wisconsin (WI)</CustomSelectItem>
                <CustomSelectItem value="WY">Wyoming (WY)</CustomSelectItem>
            </CustomSelect>
        </div>
    );
}